import axios, {AxiosInstance} from 'axios';
import Asset from '../models/Asset';
import {ApiService} from './api';

export interface VideoPosition {
    left: number;
    top: number;
    width: number;
    height: number;
}

// Wire format for POST /jobs/generate — a discriminated union on `engine`.
// Mirrors the API's CreateJobBody (a Pydantic v1 discriminated union).
//
// The Lambda dispatches on `engine`: html → Puppeteer, scene → Konva-on-Node.
// Pick the right variant at the call site (TypeScript narrows on the literal).

/** Why a generation ran. Provenance for the server-side generation record —
 *  "auto-preview" is the still ShareModal renders on open without the user
 *  asking, which is what makes explicit-generate counts meaningful.
 *  "whatsapp" is only ever READ: the WhatsApp bot writes those records itself
 *  (no stored inputs, so no retry), and the API refuses it on POST. */
export type GenerationActionSource = 'explicit' | 'auto-preview' | 'retry' | 'admin-replay' | 'whatsapp';

interface BaseGenerateJobFields {
    width: number;
    height: number;
    hasVideo?: boolean;
    videoUrl?: string;
    videoDuration?: number;
    videoPosition?: VideoPosition;
    outputFormat?: 'png' | 'mp4';
    // Generation-history provenance (contract §5). These are REFERENCES only —
    // the API verifies template access and snapshots templateName itself; names
    // sent by the client are never trusted.
    templateId?: string;
    designKey?: string;
    workspaceId?: string;
    actionSource?: GenerationActionSource;
}

export interface HtmlEngineJobRequest extends BaseGenerateJobFields {
    engine: 'html';
    html: string;
}

export interface SceneEngineJobRequest extends BaseGenerateJobFields {
    engine: 'scene';
    scene: unknown;
    values?: Record<string, unknown>;
    transforms?: Record<string, unknown>;
    /** { inputKey: 'contain' } for images the user replaced — see
     *  scene/imageFit.deriveImageFit. Absent = the template's own fit. */
    imageFit?: Record<string, string>;
    fonts?: Record<string, unknown>;
    skipElementId?: string;
    skipBackground?: boolean;
}

export type CreateJobRequest = HtmlEngineJobRequest | SceneEngineJobRequest;

export interface JobStatus {
    jobId: string;
    // 'accepted' is the new record's initial state; 'pending' is what the
    // legacy job collection reported. Both mean "not started yet".
    status: 'accepted' | 'pending' | 'processing' | 'completed' | 'failed';
    progress: number;
    progressMessage?: string;
    // Completed responses carry a FRESH presigned URL for the output —
    // videoUrl for mp4, imageUrl for png. Never persisted client-side: they
    // expire, so every read re-presigns server-side.
    videoUrl?: string;
    imageUrl?: string;
    bucket?: string;
    key?: string;
    size?: number;
    imageBase64?: string;
    errorMessage?: string;
}

export interface CreateJobResponse {
    jobId: string;
    status: string;
}

// POST /jobs/generate and GET /jobs/:id/status now require a JWT, so these
// go through the authenticated axios instance (auth header + 401→refresh
// interceptor) instead of a bare fetch. The error/response contracts are
// preserved for callers: a non-2xx response still throws an Error whose
// message is `Failed to create job: <status> - <body>` (resp. `Failed to
// get job status: ...`); a network failure (no HTTP response) rethrows the
// underlying error so pollJobUntilComplete's consecutive-failure tolerance
// still kicks in.

function bodyText(data: unknown): string {
    if (data == null) return '';
    if (typeof data === 'string') return data;
    try {
        return JSON.stringify(data);
    } catch {
        return String(data);
    }
}

export async function createJob(request: CreateJobRequest): Promise<CreateJobResponse> {
    try {
        const response = await ApiService.current().api.post<CreateJobResponse>('/jobs/generate', request);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(`Failed to create job: ${error.response.status} - ${bodyText(error.response.data)}`);
        }
        throw error;
    }
}

export async function getJobStatus(jobId: string): Promise<JobStatus> {
    try {
        const response = await ApiService.current().api.get<JobStatus>(`/jobs/${jobId}/status`);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(`Failed to get job status: ${error.response.status} - ${bodyText(error.response.data)}`);
        }
        throw error;
    }
}

// After this many ms of polling, back off from the fast interval to a
// gentler cadence — most jobs finish in the first few seconds, so the tight
// loop only helps early on; beyond that it just wastes requests over the
// long (up to 600s) budget.
const POLL_BACKOFF_AFTER_MS = 30000;
const POLL_SLOW_INTERVAL_MS = 3000;

function abortError(): Error {
    // Distinguishable from job failures / timeouts by `name === 'AbortError'`
    // (mirrors the DOMException fetch aborts throw) so callers can treat a
    // user-initiated cancel differently.
    const err = new Error('Polling aborted');
    err.name = 'AbortError';
    return err;
}

export async function pollJobUntilComplete(
    jobId: string,
    onProgress: (status: JobStatus) => void,
    timeoutMs: number = 180000,  // 3 minutes
    pollIntervalMs: number = 1000,
    signal?: AbortSignal
): Promise<JobStatus> {
    const startTime = Date.now();
    // A single failed poll must not abort the generation: mobile browsers
    // freeze backgrounded tabs and kill in-flight fetches ("Failed to
    // fetch"), so tolerate a few consecutive failures before giving up.
    const maxConsecutiveFailures = 5;
    let consecutiveFailures = 0;

    while (true) {
        if (signal?.aborted) {
            throw abortError();
        }

        let status: JobStatus | undefined;
        try {
            status = await getJobStatus(jobId);
            consecutiveFailures = 0;
        } catch (error) {
            consecutiveFailures++;
            if (consecutiveFailures >= maxConsecutiveFailures) {
                throw error;
            }
        }

        if (status) {
            onProgress(status);

            if (status.status === 'completed') {
                return status;
            }

            if (status.status === 'failed') {
                throw new Error(status.errorMessage || 'Job failed');
            }

            // Check the deadline only after a successful poll: a tab frozen
            // past the timeout still gets one look at the job's final state
            // (jobs are kept for 1h server-side) before we give up.
            if (Date.now() - startTime >= timeoutMs) {
                const minutes = Math.round(timeoutMs / 60000);
                throw new Error(`Job timed out after ${minutes} minute${minutes === 1 ? '' : 's'}`);
            }
        }

        // Gentle backoff: fast (pollIntervalMs, default 1s) for the first
        // 30s, then every 3s — the overall timeout budget is unchanged.
        const interval = Date.now() - startTime < POLL_BACKOFF_AFTER_MS
            ? pollIntervalMs
            : POLL_SLOW_INTERVAL_MS;

        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, interval));
    }
}

/**
 * Pulls a completed PNG job's output into a Blob.
 *
 * Stills go through the same async job flow as videos now, so the bytes arrive
 * as a presigned S3 URL instead of the old direct-Lambda response body. The
 * rest of the editor still expects a Blob (preview <img>, download, Web Share),
 * so fetch it here and keep every consumer unchanged. Videos deliberately do
 * NOT do this — they stream from the URL, because buffering 20-45MB routinely
 * fails on mobile.
 */
export async function fetchGeneratedImageBlob(status: JobStatus, signal?: AbortSignal): Promise<Blob> {
    if (!status.imageUrl) {
        throw new Error('No image URL in response');
    }
    const response = await fetch(status.imageUrl, {signal});
    if (!response.ok) {
        throw new Error(`Failed to fetch generated image: ${response.status}`);
    }
    return response.blob();
}

// Background Removal Job types and functions

export interface BgRemovalJobStatus {
    jobId: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress: number;
    progressMessage?: string;
    errorMessage?: string;
    asset?: Asset;
}

// AI image-edit Job types and functions
//
// The job runs a GPT image edit (+ optional background removal) server-side and
// takes 10-120s, so this poller is modelled on pollJobUntilComplete's LONG-WAIT
// behaviour (backoff + consecutive-failure tolerance) rather than on the tight
// bg-removal loop below.

export type AiEditJobState = 'pending' | 'generating' | 'removing_background' | 'completed' | 'failed';

/** Why a job failed. Distinct from the 409 codes returned when CREATING a job. */
export type AiEditErrorCode = 'moderation' | 'provider' | 'internal';

/** 409 codes returned by POST /ai-edit/jobs. */
export type AiEditConflictCode = 'quota_exceeded' | 'attempt_cap' | 'job_active' | 'not_enabled';

export interface AiEditJobStatus {
    status: AiEditJobState;
    progress: number;
    /** An i18n KEY (e.g. "aiEdit.progress.generating") — run it through t(). */
    progressMessage: string;
    errorCode?: AiEditErrorCode;
    asset?: Asset;
}

/** GET /ai-edit/jobs/active — newest in-flight or recently-completed job. */
export interface AiEditActiveJob {
    jobId: string;
    status: AiEditJobState;
    progress: number;
    progressMessage: string;
    createdAt: string;
    resultAssetId?: string;
}

/**
 * Polls GET /ai-edit/jobs/{jobId}/status until the job reaches a terminal state.
 *
 * Resolves on BOTH 'completed' and 'failed' — a failure carries an errorCode the
 * UI has to render differently (moderation vs provider vs internal), so it isn't
 * an exception. Throws only on timeout (Error) and on abort (Error with
 * name === 'AbortError', matching pollJobUntilComplete).
 *
 * The first status fetch is IMMEDIATE (no leading delay) so a dialog remounting
 * onto an already-running job — the resume-after-freeze path — paints real state
 * at once instead of after a blank interval.
 */
export async function pollAiEditUntilComplete(
    apiClient: AxiosInstance,
    jobId: string,
    onProgress: (status: AiEditJobStatus) => void,
    timeoutMs: number = 240000,  // 4 minutes — jobs run 10-120s, plus slack
    signal?: AbortSignal,
    pollIntervalMs: number = 1000
): Promise<AiEditJobStatus> {
    const startTime = Date.now();
    // Same tolerance as pollJobUntilComplete: a backgrounded mobile tab kills
    // in-flight fetches, and one dead poll must not fail the whole job.
    const maxConsecutiveFailures = 5;
    let consecutiveFailures = 0;

    while (true) {
        if (signal?.aborted) {
            throw abortError();
        }

        let status: AiEditJobStatus | undefined;
        try {
            // Thread the abort signal into the request so a cancel kills the
            // in-flight fetch immediately (axios rejects with an abort error)
            // instead of leaving it dangling.
            const response = await apiClient.get<AiEditJobStatus>(`/ai-edit/jobs/${jobId}/status`, {signal});
            status = response.data;
            consecutiveFailures = 0;
        } catch (error) {
            // A cancel-triggered abort is not a poll failure — surface it.
            if (signal?.aborted) {
                throw abortError();
            }
            consecutiveFailures++;
            if (consecutiveFailures >= maxConsecutiveFailures) {
                throw error;
            }
        }

        if (status) {
            onProgress(status);

            if (status.status === 'completed' || status.status === 'failed') {
                return status;
            }

            // Deadline checked only after a successful poll, so a frozen tab
            // still gets one look at the job's final state before we give up.
            if (Date.now() - startTime >= timeoutMs) {
                const minutes = Math.round(timeoutMs / 60000);
                throw new Error(`AI edit timed out after ${minutes} minute${minutes === 1 ? '' : 's'}`);
            }
        }

        const interval = Date.now() - startTime < POLL_BACKOFF_AFTER_MS
            ? pollIntervalMs
            : POLL_SLOW_INTERVAL_MS;

        // Abort-aware sleep: resolve the moment the signal fires instead of
        // sitting out the full interval, so cancel/unmount is responsive. The
        // loop top then re-checks signal.aborted and throws the abort error.
        await new Promise<void>((resolve) => {
            const timer = setTimeout(() => {
                signal?.removeEventListener('abort', onAbort);
                resolve();
            }, interval);
            const onAbort = () => {
                clearTimeout(timer);
                resolve();
            };
            if (signal) signal.addEventListener('abort', onAbort, {once: true});
        });
    }
}

export async function pollBgRemovalUntilComplete(
    apiClient: AxiosInstance,
    jobId: string,
    onProgress: (status: BgRemovalJobStatus) => void,
    timeoutMs: number = 120000,  // 2 minutes
    pollIntervalMs: number = 1000
): Promise<BgRemovalJobStatus> {
    const startTime = Date.now();

    while (Date.now() - startTime < timeoutMs) {
        const response = await apiClient.get<BgRemovalJobStatus>(`/bg-removal/jobs/${jobId}/status`);
        const status = response.data;
        onProgress(status);

        if (status.status === 'completed') {
            return status;
        }

        if (status.status === 'failed') {
            throw new Error(status.errorMessage || 'Background removal failed');
        }

        await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
    }

    throw new Error('Background removal timed out');
}
